import type { CSSProperties } from 'react';
import { useEffect, useState, useRef } from 'react';

interface CodeProps {
  code: string;
  language?: string;
  showLineNumbers?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function CodeView({
  code,
  language = 'text',
  showLineNumbers = false,
  className,
  style,
}: CodeProps) {
  const [html, setHtml] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    import('shiki')
      .then(({ codeToHtml }) =>
        codeToHtml(code, { lang: language, theme: 'github-dark' }).then((result) => {
          if (!cancelled) setHtml(result);
        })
      )
      .catch(() => {
        if (!cancelled) setHtml(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code, language]);

  if (html === null) {
    return (
      <pre
        className={`overflow-x-auto rounded-md bg-zinc-900 p-3 text-sm text-zinc-300${className ? ` ${className}` : ''}`}
        style={style}
      >
        <code>{code}</code>
      </pre>
    );
  }

  // Security: Shiki generates HTML from code strings via syntax highlighting —
  // no user-supplied HTML is rendered, only tokenized code output.
  return (
    <div
      ref={containerRef}
      className={`overflow-x-auto rounded-md text-sm [&_pre]:p-3 ${showLineNumbers ? '[&_.line]::before:content-[counter(line)]' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
